// ============================================================================
// COMPONENTE: ClientDriveView.jsx — v2.0 — Flat View Agrupada (Passo 24.1R)
// Art. 118 — Espelho Assimétrico SSOT
//
// UX: Visão Única. Cada Caderno de Autos é um acordeão auto-expansível.
// Sem navegação de 2 níveis. Sem botão "← Voltar".
// Upload SSOT: cada acordeão usa o folder.id do próprio Caderno como parentId.
//
// SEGURANÇA:
//   - Firebase getIdToken() injetado em TODOS os fetch (GET + POST)
//   - Zero OAuth Frontend — Service Account no backend
//   - Cliente NUNCA vê ações de excluir / renomear / criar pasta
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle, ChevronDown, Download, ExternalLink, File, FileText, FolderOpen, Image, Info, RefreshCw, Trash2, Upload, Video, X } from 'lucide-react';
import JSZip from 'jszip';
import { processDocumentForUpload, shouldProcess } from '../../utils/DocumentProcessor';
import { formatarNumero, larguraDaPeca, normalizarInicio, numeroDoAnexo } from '../../utils/numeracaoAnexos';
import { processFileToPdf } from '../../utils/imageToPdfPipeline';
import { auth } from '../../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../../config/Config';
import useCurrentUser from '../../hooks/useCurrentUser';
import {
  logDocumentRead,
  subscribeWorkspaceByFolder,
  resolvePieceKeyFromFolder,
  createTriageIntent,
  commitTriageIntent,
  fallbackTriageCommit,
  addClientNote,
  updateWorkspaceAssignment,
  deleteSagaDoc,
  renameSlot,
  setUploadLock,
  clearUploadLock,
  removeExtraSlot,
  addExtraSlot,
  subscribeWorkspacesByClient,
  serverTimestamp,
} from './ClientDriveViewRepository';
import { SIGNATURE_STATUS, markClientSignatureNotificationsRead, updateSignatureStatus } from '../../services/SignatureService';
import { useClientNotifications } from '../../services/NotificationService';
import ForensicTooltip from './ForensicTooltip';

const DRIVE_FOLDER_MIME = 'application/vnd.google-apps.folder';

const ACCEPTED_MIME = [
  'application/pdf',
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'video/mp4', 'video/webm',
];

const MAX_SIZE_MB = 50;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

// ── Helpers ──────────────────────────────────────────────────────────────────

const getBackendBaseUrl = () =>
 {
  const envUrl = String(import.meta?.env?.VITE_BACKEND_URL || '').trim();
  if (envUrl) return envUrl.replace(/\/+$/, '');
  return 'https://rda-backend-thuq.onrender.com';
};

function formatBytes(b) {
  if (!b) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(Math.max(1, b)) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}

function getMimeIcon(mimeType) {
  const m = String(mimeType || '').toLowerCase();
  if (m.includes('pdf'))       return <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />;
  if (m.startsWith('image/'))  return <Image    className="w-4 h-4 text-blue-500 flex-shrink-0" />;
  if (m.startsWith('video/'))  return <Video    className="w-4 h-4 text-purple-500 flex-shrink-0" />;
  return                               <File    className="w-4 h-4 text-slate-400 flex-shrink-0" />;
}

// ── Nomenclatura Forense (Padrão Ouro — CSJT/PJe) ──────────────────────────
// Formato: YYYY-MM-DD_XX_ANEXO_[Nome_Limpo].ext  (zero-padding, sem diacríticos)
function formatLegalFileName(originalName, description, index) {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const idx = String(index).padStart(2, '0');
  const cleanDesc = String(description || 'Documento')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_\-]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 40);
  const extIdx = String(originalName || '').lastIndexOf('.');
  const ext = extIdx !== -1 ? originalName.slice(extIdx) : '';
  return `${yyyy}-${mm}-${dd}_${idx}_ANEXO_${cleanDesc}${ext}`;
}

// ── Concurrency Pool — Max 3 uploads simultâneos (Padrão Ouro) ──────────────
// Evita asfixia TCP (limite 6 sockets do navegador) e Rate Limit 429 da Google.
const MAX_CONCURRENT_EXTRA = 3;

async function runConcurrencyPool(tasks, concurrency, onEach) {
  const results = [];
  let idx = 0;
  let active = 0;
  return new Promise((resolve) => {
    const next = () => {
      if (idx >= tasks.length && active === 0) { resolve(results); return; }
      while (active < concurrency && idx < tasks.length) {
        const i = idx++;
        active++;
        onEach(tasks[i], i)
          .then((r) => { results.push({ ok: true, index: i, result: r }); })
          .catch((e) => { results.push({ ok: false, index: i, error: e }); })
          .finally(() => { active--; next(); });
      }
    };
    next();
  });
}

// ── Agrupador de Cadernos por Prefixo (Anti-Explosão de Pastas) ─────────────
// Detecta prefixo comum no nome das pastas (ex: "CÍVEL - " em "CÍVEL - Fase 01")
// e agrupa visualmente. Sem prefixo comum → cada pasta é seu próprio bloco.
function groupFoldersByPrefix(folders) {
  if (!Array.isArray(folders) || folders.length === 0) return [];
  const getPrefix = (name) => {
    const m = String(name || '').match(/^(.+?)\s*[-—|:]\s*.+/);
    return m ? m[1].trim() : null;
  };
  const prefixMap = {};
  const ungrouped = [];
  folders.forEach((f) => {
    const prefix = getPrefix(f.name);
    if (prefix) {
      if (!prefixMap[prefix]) prefixMap[prefix] = { groupName: prefix, folders: [] };
      prefixMap[prefix].folders.push(f);
    } else {
      ungrouped.push(f);
    }
  });
  const groups = [];
  Object.values(prefixMap).forEach((g) => {
    if (g.folders.length >= 2) groups.push(g);
    else ungrouped.push(...g.folders);
  });
  ungrouped.forEach((f) => groups.push({ groupName: null, folders: [f] }));
  return groups;
}

// ── ACORDEÃO: Caderno de Autos (v2.1) ────────────────────────────────────────
// Sanfona controlada pelo parent via props isExpanded + onToggle.
// Lazy fetch: arquivos carregados apenas na primeira expansão.
function CadernoPanel({ folder, clientId, isExpanded, onToggle, onUploaded, linkedDocs = [] }) {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const [uploadOk, setUploadOk]   = useState('');
  const [pipelineActive, setPipelineActive] = useState(false);
  const [dragOver, setDragOver]   = useState(false);
  const hasFetchedRef             = useRef(false);
  const inputRef                  = useRef(null);
  const wsLoadedRef               = useRef(false);
  const uploadSlotInputRefs       = useRef({});
  const [requestedSlots, setRequestedSlots]     = useState([]);
  // Numeração dos anexos (24/09/2026): "começar em" da peça, lido do MESMO orgState que já alimenta
  // esta tela (o estado legado). A numeração do cliente continua SÓ entre os slots que ele enxerga
  // (slots ocultos não deixam "buraco": mostrar a lista completa revelaria que existem anexos ocultos).
  const [numeroInicialAnexos, setNumeroInicialAnexos] = useState(1);
  const numeroAnexoDoCliente = (slotIdx) =>
    formatarNumero(
      numeroDoAnexo(slotIdx >= 0 ? slotIdx : requestedSlots.length, numeroInicialAnexos),
      larguraDaPeca(requestedSlots.length, numeroInicialAnexos),
    );
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [uploadingSlotId, setUploadingSlotId]   = useState(null);
  const [uploadSlotProgress, setUploadSlotProgress] = useState(null);
  const [completedSlots,   setCompletedSlots]   = useState(() => new Set());
  const [assignedSlotIds,  setAssignedSlotIds]  = useState(() => new Set());
  const [assignmentsMap,   setAssignmentsMap]   = useState({});
  const [retryMsg, setRetryMsg] = useState('');
  const [extraDocName, setExtraDocName] = useState('');
  const [attachmentsFolderId, setAttachmentsFolderId] = useState('');
  const wsIdRef = useRef('');
  // ITEM G (26/08/2026): qual peça este folder.id resolve para, quando
  // determinável (ver resolvePieceKeyFromFolder). null = caderno de peça
  // única ou não determinável — grava só no legado, como sempre foi.
  const pieceKeyRef = useRef(null);
  const [workspacePieceType, setWorkspacePieceType] = useState('');
  const [workspaceUpdatedAt,  setWorkspaceUpdatedAt]  = useState('');
  const [workspaceProcessTitle, setWorkspaceProcessTitle] = useState('');
  const [workspaceNumeroCNJ,    setWorkspaceNumeroCNJ]    = useState('');
  const [workspaceParties,      setWorkspaceParties]      = useState('');
  const [lawyerFileIds, setLawyerFileIds] = useState(() => new Set());
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [loadingFileId, setLoadingFileId] = useState(null);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchDone, setBatchDone] = useState(0);
  const [batchActive, setBatchActive] = useState(false);
  const [lastUploadCount, setLastUploadCount] = useState(0);
  const [zipping, setZipping] = useState(false);
  const [zipProgress, setZipProgress] = useState('');
  const [dspeInfoOpen, setDspeInfoOpen] = useState(() => {
    try { return localStorage.getItem('rda_dspe_info_v1') !== '1'; } catch { return true; }
  });
  const extraInputRef = useRef(null);

  const VIEWABLE_MIME = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml']);

  const getZippableFiles = useCallback(() => {
    const seen = new Set();
    const result = [];
    items.filter((it) => !lawyerFileIds.has(String(it.id || ''))).forEach((it) => {
      const fid = String(it.id || '').trim();
      if (fid && !seen.has(fid)) { seen.add(fid); result.push({ id: fid, name: it.name || fid }); }
    });
    Object.values(assignmentsMap).forEach((a) => {
      const multi = Array.isArray(a?.multiFiles) && a.multiFiles.length > 0 ? a.multiFiles : (a?.id ? [a] : []);
      multi.forEach((mf) => {
        const fid = String(mf?.id || '').trim();
        if (fid && !seen.has(fid)) { seen.add(fid); result.push({ id: fid, name: mf?.name || fid }); }
      });
    });
    return result;
  }, [items, lawyerFileIds, assignmentsMap]);

  const handleDownloadZip = useCallback(async () => {
    const allFiles = getZippableFiles();
    if (allFiles.length === 0) return;
    setZipping(true);
    setZipProgress(`0/${allFiles.length}`);
    try {
      const token = await getToken(true).catch(() => null);
      if (!token) { alert('Sessão expirada. Faça login novamente.'); return; }
      const baseUrl = getBackendBaseUrl();
      const zip = new JSZip();
      let done = 0;
      for (const file of allFiles) {
        try {
          const resp = await fetch(`${baseUrl}/drive/proxy/${encodeURIComponent(file.id)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!resp.ok) continue;
          const blob = await resp.blob();
          const safeName = String(file.name || `arquivo_${done + 1}`).replace(/[\/]/g, '_');
          zip.file(safeName, blob);
        } catch { /* skip file on error */ }
        done++;
        setZipProgress(`${done}/${allFiles.length}`);
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const folderName = String(workspacePieceType || folder.name || 'Caderno').replace(/[\/:*?"<>|]/g, '_');
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${folderName}_Autos.zip`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    } catch (e) {
      alert(`Falha ao gerar ZIP: ${e?.message || e}`);
    } finally {
      setZipping(false);
      setZipProgress('');
    }
  }, [getZippableFiles, folder.name, workspacePieceType]);

  const handleViewFile = useCallback(async (fileId, fileName) => {
    setLoadingFileId(fileId);
    if (clientId && !window.__rda_portal_preview_mode) {
      try {
        const appId = String(FIREBASE_CONFIG?.projectId || '').trim();
        if (appId) {
          logDocumentRead(appId, clientId, fileId, fileName);
        }
      } catch {}
    }
    try {
      const token = await getToken(true).catch(() => null);
      if (!token) { alert('Sessão expirada. Faça login novamente.'); return; }
      const baseUrl = getBackendBaseUrl();
      const resp = await fetch(`${baseUrl}/drive/proxy/${encodeURIComponent(fileId)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`Erro ${resp.status}`);
      const blob = await resp.blob();
      const canView = VIEWABLE_MIME.has(blob.type);
      const url = URL.createObjectURL(blob);
      if (canView) {
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'arquivo';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
      }
    } catch (e) {
      alert(`Não foi possível abrir o arquivo: ${e?.message || e}`);
    } finally {
      setLoadingFileId(null);
    }
  }, []);

  const handleDeleteClientFile = useCallback(async (fileId) => {
    if (!fileId) return;
    setDeletingFileId(fileId);
    try {
      const token = await getToken().catch(() => null);
      const baseUrl = getBackendBaseUrl();
      await fetch(`${baseUrl}/drive/delete-folder`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ folderId: fileId }),
      });
      setItems((prev) => prev.filter((item) => item.id !== fileId));
    } catch {
      // silently ignore
    } finally {
      setDeletingFileId(null);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getToken().catch(() => null);
      const baseUrl = getBackendBaseUrl();
      const resp = await fetch(`${baseUrl}/drive/folder/${encodeURIComponent(folder.id)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(String(err?.error || err?.detail || 'Falha ao listar documentos.'));
      }
      const data = await resp.json();
      const files = Array.isArray(data)
        ? data.filter((i) => i.mimeType !== DRIVE_FOLDER_MIME)
        : [];
      setItems(files);
    } catch (e) {
      setError(String(e?.message || 'Erro ao carregar documentos.'));
    } finally {
      setLoading(false);
    }
  }, [folder.id]);

  useEffect(() => {
    if (isExpanded && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchItems();
    }
  }, [isExpanded, fetchItems]);

  useEffect(() => {
    const appId = String(FIREBASE_CONFIG?.projectId || '').trim();
    if (!appId) return;
    const applySnap = (docSnap) => {
      const data      = docSnap.data() || {};
      const pt  = String(data?.pieceType || '').trim();
      const upd = data?.updatedAt?.toDate?.()?.toISOString?.() || '';
      if (pt)  setWorkspacePieceType(pt);
      if (upd) setWorkspaceUpdatedAt(upd);
      const procTitle = String(data?.processTitle || data?.caseTitle || data?.title || data?.label || '').trim();
      const cnj       = String(data?.numeroCNJ || data?.cnj || data?.processNumber || data?.cnjNumber || '').trim();
      const parties   = String(data?.partiesLabel || data?.parties || data?.clientName || '').trim();
      if (procTitle) setWorkspaceProcessTitle(procTitle);
      if (cnj)       setWorkspaceNumeroCNJ(cnj);
      if (parties)   setWorkspaceParties(parties);
      const wsAttFolder = String(data?.attachmentsFolderId || data?.driveFolderId || '').trim();
      if (wsAttFolder) setAttachmentsFolderId(wsAttFolder);
      const orgState  = data?.organizerState || {};
      const clientReq = orgState?.clientRequested || {};
      const sNames    = orgState?.slotNames   || {};
      const sOrder    = Array.isArray(orgState?.slotOrder) ? orgState.slotOrder : [];
      const assignedIds = new Set([
        ...Object.values(orgState?.assignments || {}).map((v) => String(v?.id             || '')).filter(Boolean),
        ...Object.values(orgState?.assignments || {}).map((v) => String(v?.signedDriveId  || '')).filter(Boolean),
        ...Object.values(orgState?.assignments || {}).map((v) => String(v?.originalDriveId || '')).filter(Boolean),
        ...(Array.isArray(orgState?.hiddenFileIds) ? orgState.hiddenFileIds.map(String).filter(Boolean) : []),
      ]);
      setLawyerFileIds(assignedIds);
      setAssignedSlotIds(new Set(Object.keys(orgState?.assignments || {}).filter(Boolean)));
      setAssignmentsMap(orgState?.assignments || {});
      setNumeroInicialAnexos(normalizarInicio(orgState?.numeroInicialAnexos, sOrder.length));
      setRequestedSlots(
        sOrder
          .filter((id) => clientReq[String(id)] !== false)
          .map((id) => {
            const raw  = String(sNames[String(id)] || '').trim();
            const isExtra = String(id).startsWith('slot-client-extra-');
            const name = raw || (isExtra ? '' : 'Documento Solicitado');
            return { slotId: String(id), name };
          })
      );
      const sigReqs = data?.signatureRequests || {};
      const pending = Object.entries(sigReqs)
        .filter(([, v]) => v?.status === SIGNATURE_STATUS.PENDING_GOVBR || v?.status === SIGNATURE_STATUS.PENDING_CLICKSIGN)
        .map(([slotId, v]) => ({
          slotId,
          status:      v.status,
          method:      v.method || 'GOVBR',
          link:        v.link   || null,
          slotName:    String(sNames[slotId] || slotId),
          requestedAt: v.requestedAt || null,
        }));
      setPendingSignatures(pending);
    };
    const unsub = subscribeWorkspaceByFolder(appId, folder.id, (snap) => {
      wsIdRef.current = snap.ref.id;
      pieceKeyRef.current = resolvePieceKeyFromFolder(snap.data(), folder.id);
      applySnap(snap);
    });
    return () => {
      unsub();
      setRequestedSlots([]);
      setPendingSignatures([]);
      setCompletedSlots(new Set());
      setAssignedSlotIds(new Set());
    };
  }, [folder.id]);

  const handleUpload = useCallback(async (rawFile, overrideName = null, wsId = null, assignSlotId = null, extraSlotMeta = null) => {
    if (!rawFile) return;
    const mimeOk = ACCEPTED_MIME.includes(rawFile.type) || ACCEPTED_MIME.includes('image/heic');
    if (!mimeOk) {
      setUploadErr(`Formato não aceito (${rawFile.type || 'desconhecido'}). Use PDF, imagem, DOCX ou vídeo.`);
      return;
    }
    if (rawFile.size > MAX_SIZE_BYTES) {
      setUploadErr(`Arquivo muito grande (${formatBytes(rawFile.size)}). Máximo: ${MAX_SIZE_MB} MB.`);
      return;
    }
    if (!assignSlotId) setUploading(true);
    setUploadErr('');
    setUploadOk('');
    let file = rawFile;

    // ── PASSO 70: Pipeline PDF-First (HEIC/JPG/PNG → PDF A4 otimizado) ──────
    const isImageFile = rawFile.type.startsWith('image/') ||
      rawFile.name.toLowerCase().endsWith('.heic') ||
      rawFile.name.toLowerCase().endsWith('.heif');
    if (isImageFile) {
      setPipelineActive(true);
      try {
        file = await processFileToPdf(rawFile);
      } catch {
        file = rawFile;
      } finally {
        setPipelineActive(false);
      }
    } else if (shouldProcess(rawFile)) {
      try {
        file = await processDocumentForUpload(rawFile);
      } catch {
        file = rawFile;
      }
    }
    // ─────────────────────────────────────────────────────────────────────────
    let sagaDocRef = null;
    try {
      const token = await getToken(true);
      if (!token) throw new Error('Sessão expirada. Faça login novamente.');
      let safeName = overrideName
        ? String(overrideName).replace(/[/\\]/g, '_')
        : `${Date.now()}_${String(file.name || 'arquivo').replace(/[/\\]/g, '_').replace(/\s+/g, '_')}`;
      if (file.type === 'application/pdf' && !safeName.toLowerCase().endsWith('.pdf')) {
        safeName = safeName.replace(/\.[^/.]+$/, '') + '.pdf';
      }
      const baseUrl = getBackendBaseUrl();
      const mime = file.type || 'application/octet-stream';
      let driveResult = null;

      // ── SAGA Passo 1: Intent (Pré-Reserva) — registra ANTES do upload ──
      const safeClientIdIntent = String(clientId || '').trim();
      const appIdIntent = String(FIREBASE_CONFIG?.projectId || '').trim();
      if (safeClientIdIntent) {
        const intentPayload = {
          clientId:            safeClientIdIntent,
          authUid:             uid || null,
          documentType:        `Drive — ${folder.name}`,
          fileName:            safeName,
          mimeType:            mime,
          status:              'UPLOADING',
          source:              'client_portal_drive',
          is_extra_client_upload: false,
          taskRef:             null,
          intentAt:            serverTimestamp(),
          createdAt:           serverTimestamp(),
        };
        sagaDocRef = await createTriageIntent(appIdIntent, intentPayload);
      }

      // [ÔMEGA] Caminho 1: Direct-to-Cloud — chunking real via Session URI (bypassa Render)
      const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB — múltiplo exato de 256KB
      if (file.size > 0) {
        try {
          const sessionRes = await fetch(`${baseUrl}/drive/generate-resumable-session-uri`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ name: safeName, mimeType: mime, parentId: attachmentsFolderId || folder.id, fileSize: file.size }),
          });
          if (sessionRes.ok) {
            const sessionData = await sessionRes.json().catch(() => ({}));
            const sessionUri = String(sessionData?.sessionUri || '').trim();
            if (sessionUri) {
              const totalBytes = file.size;
              let offset = 0;
              const MAX_CHUNK_RETRIES = 3;

              while (offset < totalBytes) {
                const end = Math.min(offset + CHUNK_SIZE, totalBytes);
                const chunk = file.slice(offset, end);
                const isLast = end === totalBytes;
                const rangeHeader = `bytes ${offset}-${end - 1}/${totalBytes}`;

                let chunkSuccess = false;
                for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
                  try {
                    const putRes = await fetch(sessionUri, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': mime,
                        'Content-Range': rangeHeader,
                      },
                      body: chunk,
                    });

                    if (putRes.status === 200 || putRes.status === 201) {
                      driveResult = await putRes.json().catch(() => ({}));
                      console.info('[RDA] Upload Direct-to-Cloud concluído ✓', driveResult?.id);
                      offset = totalBytes;
                      chunkSuccess = true;
                      break;
                    }

                    if (putRes.status === 308) {
                      const range = putRes.headers.get('range') || '';
                      const match = range.match(/bytes=0-(\d+)/);
                      offset = match ? parseInt(match[1], 10) + 1 : end;
                      console.info(`[RDA] Chunk aceito (308). Progresso: ${Math.round((offset / totalBytes) * 100)}%`);
                      chunkSuccess = true;
                      break;
                    }

                    console.warn(`[RDA] Chunk PUT status inesperado: ${putRes.status}`);
                    if (attempt === MAX_CHUNK_RETRIES) break;
                    await new Promise((r) => setTimeout(r, 1000 * attempt));
                  } catch (chunkErr) {
                    console.warn(`[RDA] Chunk falhou (tentativa ${attempt}/${MAX_CHUNK_RETRIES}):`, chunkErr?.message);
                    if (attempt === MAX_CHUNK_RETRIES) break;
                    try {
                      const probeRes = await fetch(sessionUri, {
                        method: 'PUT',
                        headers: { 'Content-Range': `bytes */${totalBytes}` },
                      });
                      if (probeRes.status === 308) {
                        const range = probeRes.headers.get('range') || '';
                        const match = range.match(/bytes=0-(\d+)/);
                        if (match) offset = parseInt(match[1], 10) + 1;
                        console.info(`[RDA] Offset recuperado via probe: ${offset}`);
                      }
                    } catch { /* probe falhou, retry com mesmo offset */ }
                    await new Promise((r) => setTimeout(r, 2000 * attempt));
                  }
                }
                if (!chunkSuccess && offset < totalBytes) {
                  console.warn('[RDA] Chunking falhou após retries, caindo no fallback proxy.');
                  break;
                }
              }
            }
          }
        } catch (sessionErr) {
          console.warn('[RDA] Session URI indisponível, usando fallback /drive/upload-file:', sessionErr?.message);
        }
      }

      // [P87] Caminho 2: Fallback via Render (P85 NamedTemporaryFile + P86 retry)
      if (!driveResult?.id) {
        const form = new FormData();
        form.append('file', file, safeName);
        form.append('parentId', attachmentsFolderId || folder.id);
        form.append('fileName', safeName);
        form.append('makePublic', 'false');
        const MAX_ATTEMPTS = 3;
        const RETRY_STATUSES = new Set([502, 503, 504]);
        let resp;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          try {
            resp = await fetch(`${baseUrl}/drive/upload-file`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: form,
            });
          } catch (netErr) {
            if (attempt === MAX_ATTEMPTS) throw new Error('Falha de Conexão: Não foi possível comunicar com os servidores RDA na Nuvem. O documento será retido na fila.');
            const wait = 2000 * attempt;
            setRetryMsg(`Servidor em standby, a acordar... (Tentativa ${attempt}/${MAX_ATTEMPTS})`);
            console.warn(`[RDA] Tentativa ${attempt} falhou (erro de rede). Retentando em ${wait / 1000}s...`);
            await new Promise((r) => setTimeout(r, wait));
            continue;
          }
          if (RETRY_STATUSES.has(resp.status)) {
            if (attempt === MAX_ATTEMPTS) {
              setRetryMsg('');
              throw new Error('Falha de Conexão: Não foi possível comunicar com os servidores RDA na Nuvem. O documento será retido na fila.');
            }
            const wait = 2000 * attempt;
            setRetryMsg(`Servidor em standby, a acordar... (Tentativa ${attempt}/${MAX_ATTEMPTS})`);
            console.warn(`[RDA] Tentativa ${attempt} falhou com status ${resp.status}. Servidor reiniciando? Retentando em ${wait / 1000}s...`);
            await new Promise((r) => setTimeout(r, wait));
            continue;
          }
          setRetryMsg('');
          break;
        }
        const result = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(String(result?.detail || result?.error || 'Falha no upload.'));
        driveResult = result;
      }

      const driveFileId = String(driveResult?.fileId || driveResult?.id || '').trim();
      const fileUrl     = String(driveResult?.webViewLink || driveResult?.webContentLink || '').trim()
        || (driveFileId ? `gdrive://${driveFileId}` : '');

      // ── SAGA Passo 3: Commit — atualiza o Intent com dados do Drive ─────
      const safeClientId = String(clientId || '').trim();
      const appId        = String(FIREBASE_CONFIG?.projectId || '').trim();
      const actorEmail   = email || null;

      if (safeClientId && sagaDocRef) {
        const committed = await commitTriageIntent(sagaDocRef, driveFileId, fileUrl);
        if (!committed) {
          const fallbackPayload = {
            clientId: safeClientId, authUid: uid || null,
            documentType: `Drive — ${folder.name}`, fileName: safeName,
            fileUrl, downloadUrl: fileUrl, driveFileId,
            storageEngine: { provider: 'gdrive', fileId: driveFileId, webViewLink: fileUrl },
            mimeType: String(file?.type || 'application/octet-stream').trim(),
            status: 'Aguardando Triagem', source: 'client_portal_drive',
            is_extra_client_upload: false, taskRef: null, createdAt: serverTimestamp(),
          };
          await fallbackTriageCommit(appId, fallbackPayload);
        }

        if (appId) {
          await addClientNote(appId, safeClientId, {
            category:  'USER',
            type:      'PORTAL_UPLOAD_SUBMITTED',
            actor:     { name: actorEmail || 'Cliente', isBot: false },
            payload:   {
              fileName:  safeName,
              fileUrl,
              driveFileId,
              caderno:   folder.name,
              text:      `Arquivo enviado pelo cliente no caderno: ${folder.name}`,
            },
            createdAt: serverTimestamp(),
          });
        }
      }
      // ────────────────────────────────────────────────────────────────────────

      const appIdLocal = String(FIREBASE_CONFIG?.projectId || '').trim();
      if (wsId && assignSlotId && driveFileId && appIdLocal) {
        await updateWorkspaceAssignment(appIdLocal, wsId, driveFileId, safeName, fileUrl, assignSlotId, extraSlotMeta, pieceKeyRef.current);
      }

      setUploadOk('');
      if (!assignSlotId && typeof onUploaded === 'function') onUploaded(driveResult);
      await fetchItems();
      return assignSlotId ? { id: driveFileId, name: safeName, url: fileUrl } : null;
    } catch (e) {
      // ── SAGA Passo 4: Rollback — limpa Intent órfão no catch ──
      if (sagaDocRef) {
        await deleteSagaDoc(sagaDocRef);
      }
      setUploadErr(String(e?.message || 'Falha ao enviar. Tente novamente.'));
      return null;
    } finally {
      if (!assignSlotId) setUploading(false);
    }
  }, [folder.id, folder.name, clientId, onUploaded, fetchItems, attachmentsFolderId]);

  const EXTRA_DOC_SUGGESTIONS = [
    'Contrato Social', 'Comprovante de Pagamento', 'Boletim de Ocorrência',
    'Laudo Médico', 'Receituário / Atestado', 'Nota Fiscal',
    'Declaração', 'Certidão', 'Ofício', 'Outros',
  ];

  const runBatchUpload = useCallback(async (rawFiles) => {
    const trimmed = extraDocName.trim();
    if (!trimmed) { window.alert('Digite o nome do documento antes de enviar.'); return; }
    const files = Array.from(rawFiles).filter((f) => !/\.(zip|rar|7z)$/i.test(f.name));
    if (files.length < rawFiles.length) {
      setUploadErr('Arquivos compactados (.zip/.rar/.7z) foram removidos — o PJe exige formato nativo para Preview.');
    }
    if (files.length === 0) return;
    setBatchTotal(files.length);
    setBatchDone(0);
    setBatchActive(true);
    setUploading(true);
    setUploadErr('');
    setUploadOk('');
    const tasks = files.map((f, i) => ({
      file: f,
      legalName: formatLegalFileName(f.name, trimmed, i + 1),
      slotId: `slot-client-extra-${Date.now()}-${i}`,
      slotName: files.length > 1 ? `${trimmed} (${i + 1})` : trimmed,
    }));
    const results = await runConcurrencyPool(tasks, MAX_CONCURRENT_EXTRA, async (task) => {
      await handleUpload(task.file, task.legalName, wsIdRef.current, task.slotId, { name: task.slotName });
      setBatchDone((prev) => prev + 1);
    });
    const ok = results.filter((r) => r.ok).length;
    const fail = results.filter((r) => !r.ok).length;
    setBatchActive(false);
    setUploading(false);
    setLastUploadCount(ok);
    if (fail > 0) {
      setUploadErr(`Lote: ${ok} enviado(s), ${fail} falha(s).`);
    } else {
      setUploadOk(`${ok} arquivo(s) enviado(s) com sucesso!`);
    }
    setBatchTotal(0);
    setBatchDone(0);
  }, [extraDocName, handleUpload]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    if (files.length === 1) {
      const trimmed = extraDocName.trim();
      if (!trimmed) { window.alert('Digite o nome do documento antes de enviar.'); return; }
      const slotId = `slot-client-extra-${Date.now()}`;
      handleUpload(files[0], trimmed, wsIdRef.current, slotId, { name: trimmed });
    } else {
      runBatchUpload(files);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) { e.target.value = ''; return; }
    if (files.length === 1) {
      const trimmed = extraDocName.trim();
      if (!trimmed) { window.alert('Digite o nome do documento antes de enviar.'); e.target.value = ''; return; }
      const slotId = `slot-client-extra-${Date.now()}`;
      setUploading(true);
      handleUpload(files[0], trimmed, wsIdRef.current, slotId, { name: trimmed })
        .then(() => { setUploadOk(`Documento "${trimmed}" enviado com sucesso!`); setExtraDocName(''); })
        .catch(() => {})
        .finally(() => setUploading(false));
    } else {
      runBatchUpload(files);
    }
    e.target.value = '';
  };

  return (
    <div className="rounded-xl border border-gray-300 bg-white overflow-hidden shadow-md">

      {/* ── Banner Caderno de Autos — só exibe quando há número CNJ (processo ajuizado) ── */}
      {workspaceNumeroCNJ && (
        <div className="px-3 py-1.5 bg-[#20243B] flex items-center gap-2 border-b border-[#1a1e30]">
          <div className="w-1 h-3 rounded-full bg-[#C5A059] shrink-0" />
          <span className="text-[9px] font-bold text-[#C5A059] uppercase tracking-widest shrink-0">Caderno de Autos</span>
          <span className="text-[10px] font-mono text-gray-300 truncate">{workspaceNumeroCNJ}</span>
          {workspaceProcessTitle && (
            <span className="text-[10px] text-gray-400 truncate hidden sm:inline">· {workspaceProcessTitle}</span>
          )}
        </div>
      )}

      {/* ── Barra clicável da Sanfona ── */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        className="w-full flex items-center gap-3 px-4 py-3.5 bg-gray-100 hover:bg-gray-200/70 transition-colors text-left cursor-pointer"
      >
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isExpanded ? 'bg-[#C5A059]' : 'bg-[#20243B]'}`}>
          <FolderOpen className={`w-4 h-4 transition-colors ${isExpanded ? 'text-[#20243B]' : 'text-[#C5A059]'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-extrabold text-[#20243B] truncate">{workspacePieceType || folder.name || 'Caderno'}</p>
            {pendingSignatures.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[9px] font-black uppercase tracking-widest animate-pulse shrink-0">
                ✍️ {pendingSignatures.length} {pendingSignatures.length === 1 ? 'assinatura pendente' : 'assinaturas pendentes'}
              </span>
            )}
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {isExpanded
              ? (loading ? 'Carregando arquivos...' : 'Arquivos neste caderno')
              : (workspaceUpdatedAt ? `Atualizado em ${formatDate(workspaceUpdatedAt)}` : 'Clique para expandir')}
          </p>
          {requestedSlots.length > 0 && (() => {
            const total = requestedSlots.length;
            const filled = requestedSlots.filter((s) => {
              if (String(s.slotId).startsWith('procuracao-')) return completedSlots.has(s.slotId) || assignmentsMap[s.slotId]?.signed === true;
              return completedSlots.has(s.slotId) || assignedSlotIds.has(s.slotId);
            }).length;
            const pct = Math.round((filled / total) * 100);
            const allDone = filled === total;
            return (
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden" style={{ maxWidth: '120px' }}>
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${allDone ? 'bg-emerald-500' : 'bg-[#C5A059]'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className={`text-[10px] font-extrabold tabular-nums ${allDone ? 'text-emerald-600' : 'text-[#C5A059]'}`}>
                  {filled}/{total}
                </span>
              </div>
            );
          })()}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isExpanded && getZippableFiles().length > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDownloadZip(); }}
              disabled={zipping}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-extrabold uppercase tracking-widest transition-colors border ${
                zipping
                  ? 'border-yellow-300 bg-yellow-50 text-yellow-700 cursor-wait'
                  : 'border-[#C5A059]/40 bg-[#C5A059]/5 text-[#C5A059] hover:bg-[#C5A059]/15 hover:border-[#C5A059]'
              }`}
              title="Baixar todos os arquivos deste caderno como .ZIP"
            >
              {zipping
                ? <><RefreshCw className="w-3 h-3 animate-spin" /> {zipProgress}</>
                : <><Download className="w-3 h-3" /> ZIP</>}
            </button>
          )}
          {isExpanded && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); fetchItems(); }}
              disabled={loading}
              className="text-slate-400 hover:text-[#20243B] disabled:opacity-40 transition-colors"
              title="Atualizar"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* ── Corpo da Sanfona (renderizado só quando expandido) ── */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-3 space-y-4">

          {/* ── Assinaturas Pendentes (MC3) ─────────────────────────────── */}
          {pendingSignatures.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 overflow-hidden">
              <div className="px-3 py-1.5 border-b border-amber-100 flex items-center gap-1.5">
                <span className="text-[9px]">✍️</span>
                <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">Assinaturas pendentes</span>
              </div>
              {pendingSignatures.map((sig) => (
                <div key={sig.slotId} className="flex items-center gap-2 px-3 py-1.5 border-b border-amber-100/60 last:border-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                  <span className="flex-1 text-xs font-medium text-[#20243B] truncate">{sig.slotName}</span>
                  <div className="flex gap-1.5 shrink-0">
                    {sig.method === 'CLICKSIGN' && sig.link ? (
                      <a href={sig.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-600 text-white text-[10px] font-semibold hover:bg-amber-700 transition">
                        <ExternalLink className="w-2.5 h-2.5" /> Clicksign
                      </a>
                    ) : (
                      <a href="https://assinador.iti.br/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#20243B] text-white text-[10px] font-semibold hover:bg-[#1a1e30] transition">
                        <ExternalLink className="w-2.5 h-2.5" /> Gov.br
                      </a>
                    )}
                    <button type="button" onClick={() => { updateSignatureStatus(wsIdRef.current, sig.slotId, SIGNATURE_STATUS.REVIEW_PENDING); markClientSignatureNotificationsRead(String(clientId || ''), wsIdRef.current); }}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-400 bg-white text-emerald-700 text-[10px] font-semibold hover:bg-emerald-50 transition">
                      ✅ Já assinei
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Slots guiados pelo Master + Procurações (fonte única da verdade) */}
          {requestedSlots.length === 0 && wsIdRef.current && (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-4 text-center">
              <p className="text-xs text-gray-500 font-semibold">Nenhum documento pendente de envio neste caderno.</p>
              <p className="text-[10px] text-gray-400 mt-0.5">O escritório ainda não solicitou documentos aqui.</p>
            </div>
          )}
          {requestedSlots.length > 0 && (
            <div>
              <div className="flex items-center justify-end mb-1.5">
                {!dspeInfoOpen && (
                  <button type="button" onClick={() => setDspeInfoOpen(true)}
                    className="text-gray-300 hover:text-[#C5A059] transition-colors" title="Como funciona?">
                    <Info className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {dspeInfoOpen && (
                <div className="mb-3 rounded-lg border border-[#C5A059]/25 bg-amber-50/50 px-3 py-2">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-bold text-[#20243B] flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
                      Como funciona o envio de documentos
                    </span>
                    <button type="button"
                      onClick={() => { setDspeInfoOpen(false); try { localStorage.setItem('rda_dspe_info_v1', '1'); } catch {} }}
                      className="text-gray-300 hover:text-gray-500 transition-colors shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <ol className="space-y-0.5 text-xs text-gray-600 leading-snug pl-1">
                    <li><span className="text-[#C5A059] font-bold">1. </span>Esses documentos foram abertos pelo advogado para dar andamento ao processo.</li>
                    <li><span className="text-[#C5A059] font-bold">2. </span>Clique em <strong>Enviar</strong> ao lado do documento e selecione o arquivo correto.</li>
                    <li><span className="text-[#C5A059] font-bold">3. </span>Para arquivos extras fora da lista, use <strong>+ Adicionar documento extra</strong>.</li>
                    <li><span className="text-[#C5A059] font-bold">4. </span>Após o envio o item fica verde e o escritório recebe automaticamente.</li>
                  </ol>
                  <div className="mt-2 flex justify-end">
                    <button type="button"
                      onClick={() => { setDspeInfoOpen(false); try { localStorage.setItem('rda_dspe_info_v1', '1'); } catch {} }}
                      className="text-xs font-bold text-[#20243B] border border-[#C5A059]/50 rounded px-3 py-0.5 hover:bg-[#C5A059]/10 transition-colors">
                      Entendi
                    </button>
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-gray-300 overflow-hidden divide-y divide-gray-200">
              {/* Enviados (verde) primeiro, pendentes (vermelho) no final; procuracao-* sempre no topo */}
              {[...requestedSlots].sort((a, b) => {
                const aIsProc = String(a.slotId).startsWith('procuracao-');
                const bIsProc = String(b.slotId).startsWith('procuracao-');
                if (aIsProc !== bIsProc) return aIsProc ? -1 : 1;
                const aUp = String(a.slotId).startsWith('procuracao-') ? completedSlots.has(a.slotId) : (completedSlots.has(a.slotId) || assignedSlotIds.has(a.slotId));
                const bUp = String(b.slotId).startsWith('procuracao-') ? completedSlots.has(b.slotId) : (completedSlots.has(b.slotId) || assignedSlotIds.has(b.slotId));
                return aUp === bUp ? 0 : aUp ? -1 : 1;
              }).map((slot) => {
                if (String(slot.slotId).startsWith('procuracao-')) {
                  const assignment    = assignmentsMap[slot.slotId] || null;
                  const isSignedSlot  = assignment?.signed === true;
                  const openFileId    = isSignedSlot
                    ? String(assignment?.signedDriveId || assignment?.id || '').trim()
                    : String(assignment?.id || '').trim();
                  const openUrl       = isSignedSlot
                    ? String(assignment?.signedDriveUrl || assignment?.url || '').trim()
                    : String(assignment?.url || '').trim();
                  return (
                    <div key={slot.slotId} className={`flex items-center gap-2.5 px-3 py-1.5 transition-colors ${
                      isSignedSlot ? 'hover:bg-green-50/50' : 'bg-amber-50/30 hover:bg-amber-50/60'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${isSignedSlot ? 'bg-green-500' : 'bg-[#C5A059]'}`} />
                      <FileText className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                      <span className="flex-1 text-xs font-medium text-[#20243B] truncate">
                        {slot.name || (isSignedSlot ? 'Procuração Assinada' : 'Procuração')}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        {isSignedSlot ? (
                          openFileId ? (
                            <button type="button" disabled={loadingFileId === openFileId}
                              onClick={() => handleViewFile(openFileId, slot.name || 'Procuração Assinada')}
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium transition ${
                                loadingFileId === openFileId
                                  ? 'border-yellow-200 bg-yellow-50 text-yellow-600 animate-pulse'
                                  : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                              }`}>
                              {loadingFileId === openFileId
                                ? <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                                : <ExternalLink className="w-2.5 h-2.5" />}
                              Abrir
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-600">
                              <CheckCircle className="w-3 h-3" /> Enviado
                            </span>
                          )
                        ) : (
                          openFileId ? (
                            <button type="button" disabled={loadingFileId === openFileId}
                              onClick={() => handleViewFile(openFileId, slot.name || 'Procuração')}
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium transition ${
                                loadingFileId === openFileId
                                  ? 'border-yellow-200 bg-yellow-50 text-yellow-600 animate-pulse'
                                  : 'border-[#C5A059]/40 bg-white text-[#20243B] hover:bg-amber-50'
                              }`}>
                              {loadingFileId === openFileId
                                ? <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                                : <ExternalLink className="w-2.5 h-2.5" />}
                              Abrir
                            </button>
                          ) : (
                            <span className="text-[10px] text-[#C5A059] font-medium">Aguardando assinatura</span>
                          )
                        )}
                      </div>
                    </div>
                  );
                }
                const isSlotUploading = uploadingSlotId === slot.slotId;
                const isDone          = !isSlotUploading && (completedSlots.has(slot.slotId) || assignedSlotIds.has(slot.slotId));
                const slotAssignment = isDone ? (assignmentsMap[slot.slotId] || null) : null;
                const slotFileId = slotAssignment?.id || null;
                const dotCls = isDone ? 'bg-green-500' : isSlotUploading ? 'bg-yellow-400 animate-pulse' : 'bg-red-400';
                const rowCls = isDone ? 'hover:bg-green-50/40' : isSlotUploading ? 'bg-yellow-50/30' : 'hover:bg-red-50/20';
                const isClientExtra = String(slot.slotId).startsWith('slot-client-extra-');
                const slotIdx = requestedSlots.findIndex((s) => s.slotId === slot.slotId);
                const annexNum = numeroAnexoDoCliente(slotIdx);
                const baseName = (slot.name || String(slotAssignment?.name || '').replace(/^ANEXO\s*\d+\s*-\s*/, '') || '').toUpperCase();
                const displayName = (isClientExtra && slotIdx < 0) ? baseName : (baseName ? `ANEXO ${annexNum} - ${baseName}` : '');
                const files4row = isDone && slotFileId
                  ? (Array.isArray(slotAssignment?.multiFiles) && slotAssignment.multiFiles.length > 0 ? slotAssignment.multiFiles : [{ id: slotFileId, name: slotAssignment?.name || slot.name }])
                  : [];
                return (
                  <div key={slot.slotId} className={`transition-colors ${rowCls}`}>
                    {/* Main row */}
                    <div className="flex items-center gap-2.5 px-3 py-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotCls}`} />
                      <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        {!isDone && isClientExtra ? (
                          <input type="text" defaultValue={slot.name || ''} onClick={(e) => e.stopPropagation()}
                            onBlur={async (e) => { const newName = e.target.value.trim(); if (!newName || newName === slot.name) return; const appIdLocal = String(FIREBASE_CONFIG?.projectId || '').trim(); const wsId = wsIdRef.current; if (!wsId || !appIdLocal) return; await renameSlot(appIdLocal, wsId, slot.slotId, newName, pieceKeyRef.current); }}
                            className="w-full rounded border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-[#20243B] placeholder-gray-400 outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] transition-colors"
                            placeholder="Nome do documento"
                          />
                        ) : (
                          <span className="text-xs font-medium text-gray-800 truncate flex items-center gap-1">
                            {displayName || slot.name}<ForensicTooltip slotName={slot.name} />
                          </span>
                        )}
                      </div>
                      {/* Right actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        {isDone ? (
                          files4row.length > 0 ? (
                            files4row.map((mf, idx) => (
                              <button key={mf?.id || idx} type="button" disabled={loadingFileId === mf?.id}
                                onClick={() => handleViewFile(mf?.id, mf?.name || slot.name)}
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium transition ${
                                  loadingFileId === mf?.id ? 'border-yellow-200 bg-yellow-50 text-yellow-600 animate-pulse' : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                                }`}>
                                {loadingFileId === mf?.id ? <RefreshCw className="w-2.5 h-2.5 animate-spin" /> : <ExternalLink className="w-2.5 h-2.5" />}
                                {files4row.length > 1 ? `Arq. ${idx + 1}` : 'Abrir'}
                              </button>
                            ))
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-600">
                              <CheckCircle className="w-3 h-3" /> Enviado
                            </span>
                          )
                        ) : (
                          <>
                            <input ref={(el) => { if (el) uploadSlotInputRefs.current[slot.slotId] = el; }} type="file" accept={ACCEPTED_MIME.join(',')} multiple={isClientExtra} className="hidden" disabled={isSlotUploading}
                              onChange={async (e) => {
                                const uploadFiles = Array.from(e.target.files || []);
                                if (uploadFiles.length === 0) { e.target.value = ''; return; }
                                setUploadingSlotId(slot.slotId);
                                setUploadSlotProgress(uploadFiles.length > 1 ? { current: 0, total: uploadFiles.length } : null);
                                const wsId = wsIdRef.current;
                                const appIdLock = String(FIREBASE_CONFIG?.projectId || '').trim();
                                if (wsId && appIdLock) { await setUploadLock(appIdLock, wsId, slot.slotId); }
                                const slotIdx = requestedSlots.findIndex((s) => s.slotId === slot.slotId);
                                const annexNum = numeroAnexoDoCliente(slotIdx);
                                const baseName = slot.name || 'Documento Extra';
                                const batchResults = [];
                                for (let i = 0; i < uploadFiles.length; i++) {
                                  const file = uploadFiles[i];
                                  if (uploadFiles.length > 1) setUploadSlotProgress({ current: i + 1, total: uploadFiles.length });
                                  const isImg = file.type?.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif|gif|bmp|tiff?)$/i.test(file.name);
                                  const ext = isImg ? '.pdf' : (file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '');
                                  const suffix = uploadFiles.length > 1 ? ` (${i + 1}de${uploadFiles.length})` : '';
                                  const result = await handleUpload(file, `ANEXO ${annexNum} - ${baseName}${suffix}${ext}`, wsId, slot.slotId).catch(() => null);
                                  if (result?.id) batchResults.push(result);
                                }
                                if (batchResults.length > 0) { setAssignmentsMap((prev) => ({ ...prev, [slot.slotId]: { ...(prev[slot.slotId] || {}), id: batchResults[batchResults.length - 1].id, name: batchResults[batchResults.length - 1].name, url: batchResults[batchResults.length - 1].url, multiFiles: batchResults } })); setAssignedSlotIds((prev) => new Set([...prev, slot.slotId])); }
                                if (wsId && appIdLock) { await clearUploadLock(appIdLock, wsId, slot.slotId); }
                                setCompletedSlots((prev) => new Set([...prev, slot.slotId]));
                                setUploadingSlotId(null); setUploadSlotProgress(null); e.target.value = '';
                              }}
                            />
                            {isClientExtra && (
                              <button type="button" title="Remover" onClick={async (e) => { e.stopPropagation(); const appIdLocal = String(FIREBASE_CONFIG?.projectId || '').trim(); const wsId = wsIdRef.current; if (!wsId || !appIdLocal) return; await removeExtraSlot(appIdLocal, wsId, slot.slotId, pieceKeyRef.current); }}
                                className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                            <button type="button" disabled={isSlotUploading}
                              onClick={() => { if (isClientExtra && !slot.name) { window.alert('Digite o nome do documento antes de enviar.'); return; } uploadSlotInputRefs.current[slot.slotId]?.click(); }}
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium transition ${
                                isSlotUploading ? 'border-yellow-200 bg-yellow-50 text-yellow-600 cursor-wait' : 'border-red-300 bg-white text-red-600 hover:bg-red-50'
                              }`}>
                              {isSlotUploading ? <RefreshCw className="w-2.5 h-2.5 animate-spin" /> : <Upload className="w-2.5 h-2.5" />}
                              {isSlotUploading ? (uploadSlotProgress ? `${uploadSlotProgress.current}/${uploadSlotProgress.total}` : '...') : 'Enviar'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          )}

          {/* Botão Adicionar Documento Extra */}
          <div className="mt-3 flex items-center justify-between border border-dashed border-gray-400 bg-gray-50/50 rounded-lg p-2.5 hover:border-gray-500 hover:bg-gray-50 transition-all group">
            <button type="button"
              disabled={uploading || uploadingSlotId !== null}
              onClick={() => { const wsId = wsIdRef.current; const appIdLocal = String(FIREBASE_CONFIG?.projectId || '').trim(); if (!wsId || !appIdLocal) return; const id = `slot-client-extra-${Date.now()}`; addExtraSlot(appIdLocal, wsId, id, pieceKeyRef.current); }}
              className="flex items-center gap-2 text-xs font-bold text-gray-600 group-hover:text-[#C5A059] transition-colors flex-1 text-left"
            >
              <div className="w-5 h-5 rounded border border-gray-400 flex items-center justify-center bg-white flex-shrink-0 group-hover:border-[#C5A059]">
                <span className="text-lg leading-none mt-[-2px]">+</span>
              </div>
              Adicionar documento extra
            </button>
            <div className="group/tooltip relative inline-flex items-center ml-2 cursor-help">
              <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 flex-shrink-0" />
              <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-64 rounded-lg bg-[#20243B] p-3 text-white text-xs shadow-xl opacity-0 transition-opacity duration-200 group-hover/tooltip:opacity-100 group-hover/tooltip:pointer-events-auto z-50">
                <span className="block font-bold text-[#C5A059] mb-1">Documento Extra</span>
                <span className="block leading-relaxed">Use esta opção para enviar arquivos soltos que o escritório não solicitou expressamente na lista acima, mas que você considera importantes para o seu caso.</span>
                <span className="absolute top-full right-2 border-4 border-transparent border-t-[#20243B]" />
              </span>
            </div>
          </div>

          {/* Feedback */}
          {uploadErr && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700 font-semibold flex justify-between items-center gap-2">
              <span>{uploadErr}</span>
              <button type="button" onClick={() => setUploadErr('')} className="text-red-300 hover:text-red-600 flex-shrink-0">✕</button>
            </div>
          )}
          {uploadOk && (
            <div className="rounded-xl border border-green-500 bg-green-50/50 p-2.5 space-y-1.5">
              <p className="text-xs font-extrabold text-green-700 flex items-center justify-between">
                <span>✅ {uploadOk}</span>
                <button type="button" onClick={() => { setUploadOk(''); setLastUploadCount(0); }} className="text-green-300 hover:text-green-600 flex-shrink-0 text-sm">✕</button>
              </p>
              {items.filter((it) => !lawyerFileIds.has(String(it.id || ''))).slice(0, lastUploadCount || 1).map((item) => (
                <div key={item.id} className="flex items-center gap-2 rounded-lg border border-dashed border-green-300 bg-green-50/40 px-3 py-2">
                  <ExternalLink className="w-3 h-3 text-green-600 flex-shrink-0" />
                  <span className="text-[11px] font-semibold text-green-700 flex-1 min-w-0 truncate">{item.name}</span>
                  <button
                    type="button"
                    onClick={() => handleViewFile(item.id, item.name)}
                    className="flex items-center gap-1 text-[10px] font-semibold text-green-700 border border-green-300 bg-white rounded-lg px-2 py-1 hover:border-green-500 hover:bg-green-50 transition-colors flex-shrink-0"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Abrir
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Lista de arquivos */}
          {loading && (
            <div className="flex items-center gap-2 py-2 text-xs text-gray-400">
              <RefreshCw className="w-3 h-3 animate-spin" />
              Carregando arquivos...
            </div>
          )}
          {!loading && error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700 font-semibold">{error}</div>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-3">
              Os documentos enviados aparecerão aqui.
            </p>
          )}
          {items.filter((it) => !lawyerFileIds.has(String(it.id || ''))).length > 0 && (
            <ul className="divide-y divide-gray-200 rounded-xl border border-gray-300 bg-white overflow-hidden">
              {items.filter((it) => !lawyerFileIds.has(String(it.id || ''))).map((item) => (
                <li key={item.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors">
                  {getMimeIcon(item.mimeType)}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#20243B] truncate" title={item.name}>
                      {item.name || 'Sem nome'}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {formatDate(item.modifiedTime)}{item.size ? ` · ${formatBytes(Number(item.size))}` : ''}
                    </p>
                  </div>
                  {!lawyerFileIds.has(String(item.id || '')) && (
                    <button
                      type="button"
                      disabled={deletingFileId === item.id}
                      onClick={() => handleDeleteClientFile(item.id)}
                      className="flex items-center justify-center w-6 h-6 rounded-lg border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-700 transition-colors flex-shrink-0 disabled:opacity-40"
                      title="Apagar este arquivo"
                    >
                      {deletingFileId === item.id
                        ? <RefreshCw className="w-3 h-3 animate-spin" />
                        : <Trash2 className="w-3 h-3" />}
                    </button>
                  )}
                  {item.id && (
                    <button
                      type="button"
                      onClick={() => handleViewFile(item.id, item.name)}
                      className="flex items-center gap-1 text-[10px] font-semibold text-[#20243B] border border-gray-300 bg-white rounded-lg px-2 py-1 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors flex-shrink-0"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Abrir
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── COMPONENTE PRINCIPAL — Accordion Exclusivo (v2.1) ───────────────────────
// expandedFolderId controla qual sanfona está aberta (null = todas fechadas).
// Clicar numa sanfona fechada abre ela e fecha a anterior (exclusivo).
export default function ClientDriveView({ driveFolderId, clientId, onUploaded, hideHeader = false, linkedDocs = [], skipFolderFilter = false }) {
  const { getToken } = useCurrentUser();
  const [folders, setFolders]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [expandedFolderId, setExpandedFolderId] = useState(null);
  const [validFolderIds, setValidFolderIds] = useState(null);
  useClientNotifications(clientId);

  const fetchFoldersRef = useRef(null);
  const prevWsCountRef  = useRef(-1);

  const safeClientId = String(clientId || '').trim();
  useEffect(() => {
    if (!safeClientId) { setValidFolderIds(new Set()); return; }
    const appId = String(FIREBASE_CONFIG?.projectId || '').trim();
    if (!appId) { setValidFolderIds(null); return; }
    const unsub = subscribeWorkspacesByClient(appId, safeClientId, (snap) => {
      const ids = new Set();
      snap.docs.forEach((d) => {
        const data = d.data() || {};
        ['processPieceFolderId', 'attachmentsFolderId', 'driveFolderId'].forEach((field) => {
          const v = String(data?.[field] || '').trim();
          if (v) ids.add(v);
        });
      });
      setValidFolderIds(ids);
      if (prevWsCountRef.current >= 0 && snap.docs.length !== prevWsCountRef.current) {
        fetchFoldersRef.current?.();
      }
      prevWsCountRef.current = snap.docs.length;
    }, () => setValidFolderIds(null));
    return () => unsub();
  }, [safeClientId]);

  const handleToggle = (folderId) =>
    setExpandedFolderId((prev) => (prev === folderId ? null : folderId));

  const filteredFolders = skipFolderFilter || validFolderIds === null
    ? folders
    : folders.filter((f) => validFolderIds.has(String(f.id || '')));

  const safeFolderId = String(driveFolderId || '').trim();

  const fetchFolders = useCallback(async () => {
    if (!safeFolderId) return;
    setLoading(true);
    setError('');
    try {
      const token = await getToken().catch(() => null);
      const baseUrl = getBackendBaseUrl();
      const resp = await fetch(`${baseUrl}/drive/folder/${encodeURIComponent(safeFolderId)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(String(err?.error || err?.detail || 'Falha ao listar cadernos.'));
      }
      const data = await resp.json();
      const subfolders = Array.isArray(data)
        ? data.filter((i) => i.mimeType === DRIVE_FOLDER_MIME)
        : [];
      setFolders(subfolders);
    } catch (e) {
      setError(String(e?.message || 'Erro ao carregar cadernos de autos.'));
    } finally {
      setLoading(false);
    }
  }, [safeFolderId]);

  useEffect(() => { fetchFoldersRef.current = fetchFolders; }, [fetchFolders]);
  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  if (!safeFolderId) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-800 font-medium">
        Sua pasta de documentos ainda não foi configurada pelo escritório. Em breve você receberá acesso.
      </div>
    );
  }

  return (
    <div className="w-full space-y-3">

      {!hideHeader ? (
        <>
          {/* ── Cabeçalho + Atualizar ── */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-extrabold text-[#20243B] uppercase tracking-wider flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-[#C5A059]" />
              Documentos do seu caso
            </h3>
            <button
              type="button"
              onClick={fetchFolders}
              disabled={loading}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#20243B] transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
          </div>
          <p className="text-xs text-gray-500 pb-1">
            Cada caderno abaixo corresponde a um processo ou área do seu caso. Expanda para ver os documentos e enviar arquivos.
          </p>
        </>
      ) : null}

      {/* ── Estado: carregando ── */}
      {loading && (
        <div className="flex items-center gap-2 py-6 text-xs text-gray-400">
          <RefreshCw className="w-3 h-3 animate-spin" />
          Carregando cadernos...
        </div>
      )}

      {/* ── Estado: erro ── */}
      {!loading && error && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-xs text-red-700 font-semibold">
          {error}
        </div>
      )}

      {/* ── Estado: vazio ── */}
      {!loading && error && (
        <div className="rounded-2xl border border-dashed border-red-300 bg-red-50 px-4 py-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-xs text-red-700 font-extrabold">Erro de Autenticação com o Servidor</p>
          <p className="text-[10px] text-red-600 mt-1 max-w-md mx-auto">
            {error.includes('JWT') || error.includes('auth')
              ? 'O servidor não conseguiu validar sua sessão. Isso geralmente ocorre no modo preview ou quando o backend precisa ser reiniciado. Tente recarregar a página ou feche e abra o preview novamente.'
              : String(error)}
          </p>
        </div>
      )}

      {!loading && !error && filteredFolders.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
          <FolderOpen className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-xs text-gray-500 font-extrabold">Módulo em Manutenção ou Sem Cadernos Disponíveis</p>
          <p className="text-[10px] text-gray-400 mt-1">
            Nenhum caderno de autos ativo encontrado para sua conta. O escritório criará ou reativará os cadernos em breve.
          </p>
        </div>
      )}

      {/* ── Sanfona: agrupada por Processo / prefixo (Anti-Explosão de Pastas) ── */}
      {!loading && filteredFolders.length > 0 && (
        <div className="space-y-6">
          {groupFoldersByPrefix(filteredFolders).map((group, gi) => (
            <div key={gi} className="space-y-2">
              {group.groupName && (
                <div className="flex items-center gap-2 px-1">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-200" />
                  <span className="text-[10px] font-extrabold text-[#20243B] uppercase tracking-widest shrink-0 flex items-center gap-1 bg-white px-2">
                    <FolderOpen className="w-3 h-3 text-[#C5A059]" />
                    {group.groupName}
                  </span>
                  <div className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-200" />
                </div>
              )}
              <div className="space-y-2">
                {group.folders.map((folder) => (
                  <CadernoPanel
                    key={folder.id}
                    folder={folder}
                    clientId={clientId}
                    isExpanded={expandedFolderId === folder.id}
                    onToggle={() => handleToggle(folder.id)}
                    onUploaded={onUploaded}
                    linkedDocs={linkedDocs}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
